import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsPulldownService, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { take } from 'rxjs/operators';
import { PaymentsDataService, PaymentOptions } from '../../../services/payments-data.service';
import { ReceiptDetailComponent, ReceiptDetailData } from '../receipt-detail/receipt-detail.component';

// =====================================================================
// NeedsReceiptComponent — "Needs Receipt" tab of Customer Payments
// ---------------------------------------------------------------------
// 5.4 payments (Customer Management → Payments) where money moved
// (Submitted / Processing / Settled / Confirmed) but Accounting has no receipt yet.
// Since Step B2 these get a receipt automatically, so this list is normally empty —
// it catches anything the automatic step could not handle (e.g. a method with no default type).
// Pick Bank (+ Pay Type if needed) on the row → Record → the new receipt opens.
// =====================================================================

interface NeedsRow {
  OrderPaymentID: number;
  OrderID: number;
  CompanyID: number;
  CompanyName: string;
  MethodName: string | null;
  StatusName: string | null;
  Amount: number;
  FeeAmount: number | null;
  TransactionReference: string | null;
  MaskedReference: string | null;
  SubmittedDate: string | null;
  SettledDate: string | null;
  CreatedDate: string | null;
  CreatedByName: string | null;
  // editable on the row
  BankID: number | null;
  PaymentDetailTypeID: number | null;
  ActionLabel: string;
}

@Component({
  selector: 'app-needs-receipt',
  imports: [RmsModule],
  templateUrl: './needs-receipt.component.html',
  styleUrl: './needs-receipt.component.scss'
})
export class NeedsReceiptComponent implements OnInit {
  @Input() options: PaymentOptions | null = null;
  @Output() changed = new EventEmitter<void>();

  Loaded = false;
  Loading = false;
  Busy = false;
  Rows: NeedsRow[] = [];
  Columns: Column[] = [];

  Buttons: HeaderButton[] = [
    { label: 'Refresh', onAction: () => this.load() },
  ];

  constructor(
    private paymentsData: PaymentsDataService,
    private pulldownService: RmsPulldownService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    this.Columns = this.createColumns();
    await this.load();
    this.Loaded = true;
    this.cdr.markForCheck();
  }

  private get defaultBankId(): number | null {
    return (this.options?.Banks ?? []).find(b => b.IsActive)?.BankID ?? null;
  }

  async load() {
    this.Loading = true;
    try {
      const rows = await lastValueFrom(this.paymentsData.getNeedsReceipt());
      this.Rows = (rows ?? []).map((r: any) => ({
        ...r,
        BankID: this.defaultBankId,
        PaymentDetailTypeID: null,
        ActionLabel: 'Record'
      }));
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load payments that need a receipt.');
    } finally {
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  createColumns(): Column[] {
    const bankOptions = (this.options?.Banks ?? []).filter(b => b.IsActive).map(b => ({ label: b.BankName, value: b.BankID }));
    const typeOptions = (this.options?.DetailTypes ?? []).filter(d => d.IsActive && !d.IsWithdrawal && d.PostsToLedger)
      .map(d => ({ label: d.DetailTypeName, value: d.PaymentDetailTypeID }));

    return [
      { key: 'OrderPaymentID', label: '5.4 Payment #', type: 'number', hidden: false },
      { key: 'OrderID', label: 'Order #', type: 'number', hidden: false },
      { key: 'CompanyName', label: 'Customer', hidden: false },
      { key: 'MethodName', label: 'Method', type: 'select', SearchType: 'select', hidden: false },
      { key: 'StatusName', label: 'Status', type: 'select', SearchType: 'select', hidden: false },
      { key: 'Amount', label: 'Amount', type: 'currency', align: 'right', format: '1.2-2', hidden: false },
      { key: 'FeeAmount', label: 'Fee', type: 'currency', align: 'right', format: '1.2-2', hidden: false },
      { key: 'SettledDate', label: 'Settled', type: 'date', SearchType: 'date', hidden: false },
      { key: 'CreatedDate', label: 'Created', type: 'date', SearchType: 'date', hidden: false },
      { key: 'CreatedByName', label: 'By', hidden: false },
      { key: 'BankID', label: 'Bank', type: 'select', edit: true, selectOptions: bankOptions, hidden: false },
      { key: 'PaymentDetailTypeID', label: 'Pay Type (if needed)', type: 'select', edit: true, selectOptions: typeOptions, hidden: false },
      {
        key: 'ActionLabel', label: 'Action', hidden: false,
        clickable: true, onActionKey: 'record', onAction: (row: NeedsRow) => { this.record(row); },
        cellClass: 'pay-action pay-action-record'
      },
      { key: 'TransactionReference', label: 'Reference', hidden: true },
      { key: 'MaskedReference', label: 'Masked Ref', hidden: true },
      { key: 'SubmittedDate', label: 'Submitted', type: 'date', SearchType: 'date', hidden: true },
    ];
  }

  async record(row: NeedsRow) {
    if (this.Busy) return;
    if (!row.BankID) { this.msg.warn('Choose the bank first.'); return; }
    this.Busy = true;
    try {
      const r = await lastValueFrom(this.paymentsData.recordFromOrderPayment({
        OrderPaymentID: row.OrderPaymentID,
        BankID: row.BankID,
        PaymentDetailTypeID: row.PaymentDetailTypeID ?? null
      }));
      this.msg.success(r?.AlreadyLinked
        ? `5.4 payment ${row.OrderPaymentID} already had receipt #${r?.ChargeLogID}.`
        : `Receipt #${r?.ChargeLogID} recorded for 5.4 payment ${row.OrderPaymentID}.`);
      this.changed.emit();
      await this.load();
      if (r?.ChargeLogID) this.openReceipt(r.ChargeLogID);   // enter the deposit date and post
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'The receipt could not be recorded.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  openReceipt(chargeLogId: number) {
    const data: ReceiptDetailData = { ChargeLogID: chargeLogId, Options: this.options };
    const ref = this.pulldownService.open(ReceiptDetailComponent, {
      data,
      title: `Receipt #${chargeLogId}`,
      width: '980px',
      maxWidth: '96vw',
      center: true
    });
    ref.afterClosed().pipe(take(1)).subscribe(() => this.changed.emit());
  }
}
